/* eslint-disable no-console */
import React, { useCallback, useRef, useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Card, Tag, Spin, Switch } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { usePostMonitoringCommandMutation } from "../../../redux/monitoring/monitoringAPI";
import { updateSendCommand } from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import { decodeUiMessage } from "../../../flatbuffers/decoders/uiMessageDecoder";
import { createUiCmdPayload } from "../../../flatbuffers/builders/uiCmdBuilder";
// MONITORING STATE
import { useBankDispatch } from "../socketMessageState/BankMessagesContext";
// SOCKET EMITTER
import socketEmitter from "./MonitoringWebSocketEmitter";
// AUTHORIZATION
import GETJwtTokenSocket from "../../../redux/authentication/authenticationSocket";
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../app/store";

function MonitoringWebSocketConnection({
	serverId,
	serverName,
	channelType,
	liftName,
	liftId,
	channelId,
}: {
	serverId: string;
	serverName: string;
	channelType: string;
	liftName: string;
	liftId: string;
	channelId: string;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const socket = useRef<WebSocket | null>(null);
	const SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_EDGE_SERVER;
	const myIntervalCheckSocketConnection: any = useRef(null);
	// WebSocket states
	const CONNECTING = 0;
	const OPEN = 1;
	const CLOSING = 2;
	const CLOSED = 3;
	const dispatch = useBankDispatch();
	const dispatchRedux = useDispatch();

	// ************************************************ */
	// WEB SOCKET CONNECTIONS ************************* */
	/**
	 * GET Current Ready State = socket.current?.readyState
	 * WebSocket.CONNECTING... = 0
	 * WebSocket.OPEN......... = 1
	 * WebSocket.CLOSING...... = 2
	 * WebSocket.CLOSED....... = 3
	 */

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [enabledAutoRefresh, setEnabledAutoRefresh] = useState<boolean>(true);
	const [enabledSendPing, setEnabledSendPing] = useState<boolean>(true);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { showQueueInfo, stopSocketConnection, sendCommand } = useSelector((state: RootState) => state.monitoring);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [postCommand, { isLoading: isLoadingPostCommand }] = usePostMonitoringCommandMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const checkSocketConnection = () => {
		if (socket.current?.readyState === CLOSED && enabledAutoRefresh) {
			// eslint-disable-next-line no-use-before-define
			onConnect();
		}
	};

	const onMessageFlatBuffer = useCallback(async (socketMessage: ArrayBuffer | Blob | string) => {
		if (typeof socketMessage === "string") {
			console.error("❌ WS sent STRING instead of binary:", socketMessage);
			return;
		}
		if (socketMessage instanceof Blob) {
			console.warn("⚠️ Received BLOB — converting to ArrayBuffer");
			// eslint-disable-next-line no-param-reassign
			socketMessage = await socketMessage.arrayBuffer();
		}
		const bytes = new Uint8Array(socketMessage);
		try {
			const msg = decodeUiMessage(bytes);
			dispatch({
				type: "UPDATE_MESSAGE",
				payload: {
					bank_id: msg?.record?.bank_id,
					lift_id: msg?.record?.lift_id,
					car_calls: msg?.record?.car_calls,
					hall_calls: msg?.record?.hall_calls,
					faults: (msg?.record?.faults?.length ?? 0) > 0,
					position: msg?.record?.position,
					service_mode: msg?.record?.service_mode?.value ?? "",
					direction: msg?.record?.direction,
					door_status: msg?.record?.door_status,
					connected: !(
						(msg?.record?.msg?.content?.message?.payload?.message?.value === "Connected" &&
							!msg?.record?.msg?.active) ||
						socket.current?.readyState === WebSocket.CLOSED
					),
					msg: {
						active: msg?.record?.msg?.active ?? false,
						type: msg?.record?.msg?.content?.type ?? "Status",
						value: null,
					},
				},
			});
		} catch (err) {
			console.error("❌ Decoder FAILED (data arrived but is INVALID)", err);
		}
	}, []);

	const refreshAll = async () => {
		const token = await GETJwtToken();
		const BODY = {
			token,
			body: {
				lift_name: liftName,
				lift_id: liftId,
				channel_id: "",
				key_id: "",
				target_value: 0,
			},
			serverId,
		};
		postCommand(BODY);
	};

	// ************************************************ */
	// TIME INTERVAL ********************************** */
	const onStartIntervalCheckSocketConnection = () => {
		myIntervalCheckSocketConnection.current = setInterval(() => {
			checkSocketConnection();
		}, 30 * 1000);
	};

	// ************************************************ */
	// USE CALLBACKS ********************************** */
	const onSocketOpen = useCallback(() => {
		if (socket.current?.readyState === OPEN) {
			socket.current?.send(`Check First connection server ${serverId}`);
		}
		if (channelType !== "ADAPTER") {
			refreshAll();
		}
	}, []);

	const onSocketClose = useCallback(() => {
		// setIsConnected(false);
	}, []);

	const onConnect = useCallback(async () => {
		if (socket.current?.readyState !== WebSocket.OPEN) {
			const token = await GETJwtTokenSocket();
			socket.current = new WebSocket(`${SOCKET_URL}?serverID=${serverId}&access_token=${token}
`);
			socket.current.addEventListener("open", onSocketOpen);
			socket.current.addEventListener("close", onSocketClose);
			socket.current.addEventListener("message", (event: any) => {
				onMessageFlatBuffer(event.data);
			});
		}
	}, []);

	const onDisconnect = () => {
		if (socket.current?.readyState === WebSocket.OPEN) {
			socket.current.send(`DISCONNECT connection server ${serverId}`);
		} else {
			// eslint-disable-next-line no-console
			console.warn("WebSocket is not open. Skipping DISCONNECT message.");
		}
		socket.current?.close();
	};

	// ************************************************* */
	// USE EFFECT ************************************** */

	useEffect(() => {
		if (serverId) {
			onConnect();
			onStartIntervalCheckSocketConnection();
		}
		return () => {
			socketEmitter.removeAllListeners("sendCommand");
			socket.current?.close();
		};
	}, []);

	useEffect(() => {
		if (stopSocketConnection) {
			onDisconnect();
		} else {
			onConnect();
		}
	}, [stopSocketConnection]);

	useEffect(() => {
		if (sendCommand.sendCommand) {
			if (socket.current?.readyState === WebSocket.OPEN) {
				const payload = createUiCmdPayload({
					message: sendCommand.body.command,
					position: sendCommand.body.position,
					active: sendCommand.body.active,
					channelId,
					bankId: sendCommand.body.bankId,
					liftId: sendCommand.body.liftId,
				});
				socket.current.send(payload);
				dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
			}
		}
	}, [sendCommand]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Spin spinning={isLoadingPostCommand}>
			{showQueueInfo && (
				<div>
					<Card
						title={serverName}
						style={{ width: 550, marginLeft: "15px", marginTop: "10px" }}
						extra={
							<div>
								{socket.current?.readyState !== OPEN && (
									<Button onClick={onConnect} disabled={socket.current?.readyState === OPEN}>
										Connect
									</Button>
								)}
								{socket.current?.readyState === OPEN && (
									<Button onClick={onDisconnect} disabled={socket.current?.readyState !== OPEN}>
										Disconnect
									</Button>
								)}
								<Switch
									onChange={(e) => {
										setEnabledAutoRefresh(e);
									}}
									checked={enabledAutoRefresh}
									checkedChildren='Reconnection'
									unCheckedChildren='Reconnection'
									style={{ marginLeft: "10px" }}
								/>
								<Switch
									onChange={(e) => {
										setEnabledSendPing(e);
									}}
									checked={enabledSendPing}
									checkedChildren='Ping'
									unCheckedChildren='Ping'
									style={{ marginLeft: "10px" }}
								/>
							</div>
						}
					>
						<p style={{ marginBottom: "-1px" }}>
							<b
								className={`${socket.current?.readyState === OPEN ? "generalStyles__info" : "generalStyles__warning"}`}
							>
								Status:
							</b>{" "}
							<b style={{ color: "white" }}>
								{socket.current?.readyState === OPEN && <Tag color='green'>OPEN</Tag>}
								{socket.current?.readyState === CONNECTING && <Tag color='cyan'>CONNECTING</Tag>}
								{socket.current?.readyState === CLOSING && <Tag color='orange'>CLOSING</Tag>}
								{socket.current?.readyState === CLOSED && <Tag color='red'>CLOSED</Tag>}
							</b>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b
								className={`${socket.current?.readyState === OPEN ? "generalStyles__info" : "generalStyles__warning"}`}
							>
								Socket Connection:
							</b>{" "}
							<span>EDGE</span>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b
								className={`${socket.current?.readyState === OPEN ? "generalStyles__info" : "generalStyles__warning"}`}
							>
								Server:
							</b>
							<span>{serverId}</span>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b
								className={`${socket.current?.readyState === OPEN ? "generalStyles__info" : "generalStyles__warning"}`}
							>
								Channel Type:
							</b>
							<span>{channelType}</span>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b
								className={`${socket.current?.readyState === OPEN ? "generalStyles__info" : "generalStyles__warning"}`}
							>
								Check socket connection:
							</b>
							<span>15sc</span>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b
								className={`${socket.current?.readyState === OPEN ? "generalStyles__info" : "generalStyles__warning"}`}
							>
								Ping:
							</b>
							<span>30sc</span>
						</p>
					</Card>
				</div>
			)}
		</Spin>
	);
}

export default MonitoringWebSocketConnection;
